import { PAYWALL_RESULT, RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { Events, Params, useAnalytics } from '../AppAnalyticsProvider';
import { useAppState } from '../AppListenerProvider';
import { AppContext } from '../AppStateProvider';
import { SubscriptionType } from '../models/subscriptionType';

interface UseRevenueCatPaywallOptions {
    entitlementIdentifier?: string;
    redirectToShop?: boolean;
    trackAnalytics?: boolean;
    productName?: string;
    /**
     * The screen the user was on when the paywall was triggered.
     * Used to populate the `source_screen` custom dimension so the paywall
     * panel can break down conversion by entry point.
     * Callers should pass one of the Screens.* constants.
     */
    sourceScreen?: string;
    onLoadingChange?: (productName: string, isLoading: boolean) => void;
    // Required state values - hook returns early if any are missing
    userId: string | undefined;
    userBisk: number | undefined;
    username: string | undefined;
}

/**
 * Map RevenueCat's PAYWALL_RESULT to a stable `dismissed_at_step` value.
 * The native RevenueCat paywall UI doesn't emit mid-flow events, so this
 * is the best granularity we get for the paywall funnel.
 */
const mapResultToDismissalStep = (result: PAYWALL_RESULT): string => {
    switch (result) {
        case PAYWALL_RESULT.CANCELLED: return 'cancelled';
        case PAYWALL_RESULT.ERROR: return 'error';
        case PAYWALL_RESULT.NOT_PRESENTED: return 'not_presented';
        case PAYWALL_RESULT.RESTORED: return 'restored';
        default: return 'unknown';
    }
};

interface PaywallResult {
    success: boolean;
    result?: PAYWALL_RESULT;
    error?: any;
}

export const useRevenueCatPaywall = (options: UseRevenueCatPaywallOptions) => {
    const {
        entitlementIdentifier = 'pro',
        trackAnalytics = true,
        redirectToShop = false,
        productName = PRODUCT_NAMES.TOW_ROPE_PASS,
        sourceScreen = 'unknown',
        onLoadingChange,
        userId,
        userBisk,
        username
    } = options;

    const { state, dispatch } = useContext(AppContext);
    const { isNative } = useAppState();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { trackEvent } = useAnalytics();
    const [isLoading, setIsLoading] = useState(false);

    // Early return if required state is not available
    // Check for empty strings as well as undefined/null
    const hasValidUserId = userId && userId.trim() !== '';
    const hasValidUserBisk = userBisk !== undefined && userBisk !== null;
    const hasValidUsername = username && username.trim() !== '';

    const isReady = hasValidUserId && hasValidUserBisk && hasValidUsername;

    // Create a no-op function when not ready
    const presentPaywallNotReady = async (): Promise<PaywallResult> => {
        console.warn('Cannot present paywall: Required user state not available', {
            hasUserId: !!userId,
            hasValidUserId,
            hasBisk: userBisk !== undefined && userBisk !== null,
            hasValidUserBisk,
            hasUsername: !!username,
            hasValidUsername,
            userId,
            userBisk,
            username
        });
        return {
            success: false,
            error: 'User state not ready. Please wait for authentication to complete.'
        };
    };

    const presentPaywall = async (): Promise<PaywallResult> => {
        if (!isNative) {
            history.push('/shop');
            return {
                success: false,
                error: 'Not a native platform'
            };
        }

        try {
            // Set loading state
            setIsLoading(true);
            if (productName && onLoadingChange) {
                onLoadingChange(productName, true);
            }

            // Track analytics if enabled
            if (trackAnalytics) {
                // Fire PAYWALL_PRESENTED + BEGIN_CHECKOUT at the same time.
                // RevenueCat's native UI doesn't expose separate CTA-click events,
                // so this is the most granular signal we get for "user entered the
                // purchase flow." BEGIN_CHECKOUT is a GA4-standard ecommerce event
                // that unlocks native GA revenue reports without extra setup.
                const paywallParams: Record<string, any> = {
                    [Params.SOURCE_SCREEN]: sourceScreen,
                    [Params.ENTITLEMENT_ID]: entitlementIdentifier,
                    [Params.SUBSCRIPTION_TYPE]: productName,
                    // Current subscription level logged with its existing param key
                    // (not yet promoted to Params constant — 1 ad-hoc remaining).
                    subscription_level: state.subscriptionLevel,
                };
                trackEvent(Events.PAYWALL_PRESENTED, paywallParams);
                trackEvent(Events.BEGIN_CHECKOUT, paywallParams);
            }

            // Present the paywall
            const result = await RevenueCatUI.presentPaywallIfNeeded({
                requiredEntitlementIdentifier: entitlementIdentifier,
            });

            // Handle successful purchase
            if (result.result === PAYWALL_RESULT.PURCHASED) {
                await handlePurchaseSuccess();

                if (trackAnalytics) {
                    // GA4-standard `purchase` event unlocks native revenue dashboards.
                    // Also fire domain-specific PAYWALL paths for our own breakdown.
                    trackEvent(Events.PURCHASE, {
                        [Params.SOURCE_SCREEN]: sourceScreen,
                        [Params.ENTITLEMENT_ID]: entitlementIdentifier,
                        [Params.SUBSCRIPTION_TYPE]: productName,
                    });
                }

                return { success: true, result: result.result };
            }

            // Handle other results (cancelled, error, not_presented, restored)
            if (trackAnalytics && result.result !== PAYWALL_RESULT.NOT_PRESENTED) {
                trackEvent(Events.PAYWALL_DISMISSED, {
                    [Params.SOURCE_SCREEN]: sourceScreen,
                    [Params.ENTITLEMENT_ID]: entitlementIdentifier,
                    [Params.SUBSCRIPTION_TYPE]: productName,
                    [Params.DISMISSED_AT_STEP]: mapResultToDismissalStep(result.result),
                });
            }

            return { success: false, result: result.result };

        } catch (error) {
            console.error('Error presenting paywall:', error);

            if (trackAnalytics) {
                // Use the structured ERROR event with a bounded error_code taxonomy.
                // Raw error messages can leak file paths or user input → PII risk.
                trackEvent(Events.ERROR, {
                    [Params.ERROR_CODE]: 'paywall_present_failed',
                    [Params.SOURCE_SCREEN]: sourceScreen,
                    [Params.ENTITLEMENT_ID]: entitlementIdentifier,
                    [Params.SUBSCRIPTION_TYPE]: productName,
                });
            }

            return { success: false, error };

        } finally {
            // Clear loading state
            setIsLoading(false);
            if (productName && onLoadingChange) {
                onLoadingChange(productName, false);
            }
        }
    };

    const handlePurchaseSuccess = async () => {
        try {
            // Update app state
            dispatch({ type: 'setSubscriptionLevel', payload: SubscriptionType.Premium });
            dispatch({ type: 'setIsSubscribed', payload: true });

            // Invalidate subscription queries to force refresh
            await queryClient.invalidateQueries({
                queryKey: ['subscription', userId || username]
            });

            // Navigate to purchase success page
            const queryParams = new URLSearchParams({
                infd_id: userId,
                old_bisk: userBisk.toString(),
                username: username
            });

            history.push(`/purchase?${queryParams.toString()}`);

        } catch (error) {
            console.error('Error handling purchase success:', error);
            throw error;
        }
    };

    return {
        presentPaywall: isReady ? presentPaywall : presentPaywallNotReady,
        handlePurchaseSuccess,
        isLoading,
        isReady,
    };
};

// Product name constants for easy reference
export const PRODUCT_NAMES = {
    TOW_ROPE_PASS: 'Tow Rope Pass'
} as const;

export type ProductName = typeof PRODUCT_NAMES[keyof typeof PRODUCT_NAMES];
